// securityApi.ts · v12.10.30 (#2256 SecurityMe.features - the practice's feature switches; #2255 practice offline) · 2026-11-03 · VoiceSpectrum AI
// securityApi.ts · v12.10.30 (#2234 UserRow.longSignIn) · 2026-11-03 · VoiceSpectrum AI
// securityApi.ts · v12.10.26 (#2129 invite sendBy + delivery; #2132 resend answers per channel; #2133 inviteLink; #2138 UserRow.vsSupport) · 2026-10-30 · VoiceSpectrum AI
// securityApi.ts · v12.10.25 (#2106 firstRun / saveFirstRun / resetFirstRun) · 2026-10-30 · VoiceSpectrum AI
// securityApi.ts · v12.10.23 (#2071 UserRow.signedInAt / lastSeenAt / presence - who is signed in now) · 2026-10-29 · VoiceSpectrum AI
// securityApi.ts · v12.10.11.1 (#1447 invite returns inviteSent; request options) · 2026-10-09
import { normMix, type CharMix } from '@shared-ui/commonPasswords';   // v12.10.9.4 (#1342)
// v12.10.9.3 (#1329): Support access - getSupportAccess / putSupportAccess; me.support (level, reason) for a VoiceSpectrum Support session.
// v12.10.9.1: #1288 Policy.trustDeviceDays + trustedDevice / forgetDevice; #1292 lockInfo.
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// securityApi.ts - the ONE module for every Settings › Security call (T2 Security API, CONTRACT v1.0 §0 + §2) plus the
// bearer-side /api/auth endpoints the Security screens and "Your account" use (§1: confirm, change-password, 2-step setup,
// backup codes). Shapes are the contract's, camelCase. Every call goes through the shared `api` (base URL + bearer).
//
// Errors arrive as ApiError (lib/api.ts) whose body is { error, message? }. Helpers at the bottom classify them:
//   428 confirm_required  -> handled generically by withConfirm() in components/security/ConfirmItsYou.tsx
//   403 forbidden         -> "you don't have access"
//   409 escalation        -> "more than your own access"
//   409 last_admin        -> "last person who can manage users & groups"
import { api, ApiError } from './api';

// ── §0 shared shapes ──────────────────────────────────────────────────────────
export type Tier = 'view' | 'work' | 'full';
export type Level = 'none' | 'view' | 'work' | 'full' | 'custom';
export interface AreaAction { key: string; label: string; tier: Tier; help?: string }
export type AreaGroup = 'Messages' | 'Contacts' | 'Reports' | 'Settings' | 'Security';
export interface Area {
  key: string;
  group: AreaGroup;
  section?: string;          // settings area, e.g. 'AI Agent'
  name: string;
  future?: boolean;
  actions: AreaAction[];
  levels: Level[];           // levels this row offers
}
export type Grants = Record<string, string[]>;   // area key -> action keys; the level is DERIVED, never stored
export interface Group {
  id: number; name: string; stdKey: string | null; builtin: boolean; description: string;
  grants: Grants; memberCount: number; exceedsMe: boolean;
}
export interface GroupTemplate { key: 'auditor' | 'readonly'; name: string; description: string; grants: Grants }
export type UserStatus = 'active' | 'invited' | 'expired' | 'disabled';
export type SecondStepMethod = 'sms' | 'totp';
export interface UserRow {
  id: number; firstName: string; lastName: string; name: string;
  email: string | null; mobile: string | null; jobTitle: string | null;
  status: UserStatus;
  lockedUntil: string | null; groups: number[]; methods: SecondStepMethod[]; backupLeft: number;
  lastLoginAt: string | null; invitedAt: string | null; inviteExpiresAt: string | null;
  invitedBy: string | null; requestedBy: string | null; self: boolean;
  groupNames?: string[];   // v12.10.9: names of `groups`, in order (Users list + Shell card)
  photo?: string | null;   // v12.10.9.3 (#1306): the person's picture (data: URL); null = initials
  // v12.10.23 (#2071): signed in now - the newest open session (two browsers = one): since when, the last API call, active / idle (10 min)
  signedInAt?: string | null; lastSeenAt?: string | null; presence?: 'active' | 'idle' | null;
  vsSupport?: boolean;   // v12.10.26 (#2138): only ever true on your own row - VS Support people are hidden from the practice
  /** v12.10.30 (#2234): the long sign-in VoiceSpectrum Support gave this person - null = none. */
  longSignIn?: { on: boolean; by?: string | null; at?: string | null; reason?: string | null; hours: number } | null;
}
/** v12.10.26 (#2129): Invite user › Send invite by. */
export type InviteSendBy = 'email' | 'text' | 'both';
/** v12.10.26 (#2132 #2129): how one invite send went - per channel, with the reason in plain words when something did not go. */
export interface InviteDelivery { emailTried: boolean; emailSent: boolean; textTried: boolean; textSent: boolean; problem: string | null }
export type InviteAnswer = UserRow & { inviteSent?: boolean; delivery?: InviteDelivery };
export interface ActivityItem { action: string; title: string; detail?: string | null; at: string }   // v12.10.9.3 (#1307)
export type MfaRequired = 'off' | 'optional' | 'settings' | 'all';
export type ExpiryDays = 0 | 30 | 60 | 90 | 180 | 365;
export type TrustDays = 0 | 7 | 14 | 30;   // v12.10.9.1 (#1288)
export interface Policy {
  idleMinutes: number; absoluteHours: number; sessionsPerPerson: number;
  rememberDevice: boolean; rememberDays: number; rememberSkipsSecondStep: false;
  mfaRequired: MfaRequired; graceDays: number; enforceOn: string;
  trustDeviceDays: TrustDays;   // v12.10.9.1 (#1288, S47)
  methods: { sms: boolean; totp: boolean; backup: boolean; otp: boolean };
  confirmWindowMinutes: number;
  minLength: number; reuse: number; expiryDays: ExpiryDays; complexity: boolean; charMix: CharMix;   // v12.10.9.4 (#1342)
  lockAttempts: number; lockMinutes: number; selfReset: boolean;
  breachedCheck: 'future' | 'always';
}
export interface LockInfo {
  lockedUntil: string | null; lockedAt: string | null; reason: string | null;
  attempts: { at: string; what: string; userAgent: string | null; ip: string | null }[];
}
export interface SecurityMe { user: UserRow; access: Grants; isSupport: boolean; policy: Policy; support?: { level: 'config' | 'config_rw'; reason?: string | null; expiresAt?: string | null } | null;
  /** v12.10.30 (#2256): the practice's feature switches set in Support Ops (key -> on): ai, voicemail, call_back, text_reply, text_workspace. */
  features?: Record<string, boolean>;
  /** v12.10.30 (#2255): Support Ops took the practice offline (a support visit still works - the support bar says so). */
  practiceOffline?: { callOption: 'voicemail' | 'keep' | 'none'; callLabel: string; since?: string | null } | null }

// v12.10.9.3 (#1329): Settings › Security › Platform › Support access
export type SupportState = 'granted' | 'blocked';
export type SupportLevel = 'config' | 'config_rw';
export type SupportDuration = '1h' | '1d' | '1w' | '2w' | 'indefinite';
export interface SupportAccess { state: SupportState; level: SupportLevel; duration: SupportDuration; until?: string | null; endedAt?: string | null; changedAt?: string | null; changedBy?: string | null }
export interface SupportVisit { started: string; ended?: string | null; reason?: string | null; changes: number }
export interface SupportAccessView { access: SupportAccess; canChange: boolean; isSupport: boolean; visits: SupportVisit[] }

/** Contract defaults (S23/S24/S36) - used only to fill a field the API left out. */
export const POLICY_DEFAULTS: Policy = {
  idleMinutes: 60, absoluteHours: 12, sessionsPerPerson: 3,
  rememberDevice: true, rememberDays: 30, rememberSkipsSecondStep: false,
  mfaRequired: 'all', graceDays: 30, enforceOn: '', trustDeviceDays: 7,
  methods: { sms: true, totp: true, backup: true, otp: true },
  confirmWindowMinutes: 15,
  minLength: 12, reuse: 5, expiryDays: 0, complexity: false, charMix: 'any',
  lockAttempts: 5, lockMinutes: 15, selfReset: true,
  breachedCheck: 'future',
};

export function normalizePolicy(p: Partial<Policy> | null | undefined): Policy {
  const src = p ?? {};
  return {
    ...POLICY_DEFAULTS,
    ...src,
    methods: { ...POLICY_DEFAULTS.methods, ...(src.methods ?? {}) },
    rememberSkipsSecondStep: false,
    breachedCheck: 'always',   // v12.10.9.4 (#1342): common / leaked passwords are always refused
    charMix: normMix((src as { charMix?: unknown }).charMix, src.complexity),
    minLength: Math.max(12, src.minLength ?? 12),
  };
}

const T2 = '/api/t2/security';
const AUTH = '/api/auth';

// ── §2 T2 Security API ────────────────────────────────────────────────────────
export const securityApi = {
  me: () => api.get<SecurityMe>(`${T2}/me`),
  // v12.10.9.1 (#1288): this browser's 2-step trust
  trustedDevice: (token: string) => api.post<{ trusted: boolean; until?: string | null }>(`${T2}/account/trusted-device`, { token }),
  forgetDevice: (token: string | null) => api.post<{ forgotten: number }>(`${T2}/account/forget-device`, { token }),
  catalog: () => api.get<{ areas: Area[] }>(`${T2}/catalog`),

  getPolicy: () => api.get<Policy>(`${T2}/policy`),
  getSupportAccess: () => api.get<SupportAccessView>(`${T2}/support-access`),
  putSupportAccess: (a: { state: SupportState; level: SupportLevel; duration: SupportDuration }) =>
    api.put<{ access: SupportAccess; endedSessions: number }>(`${T2}/support-access`, a),
  putPolicy: (p: Partial<Policy>) => api.put<Policy>(`${T2}/policy`, p),
  signOutEveryone: () => api.post<{ signedOut: number }>(`${T2}/sign-out-everyone`),

  users: () => api.get<UserRow[]>(`${T2}/users`),
  // v12.10.11.1 (#1447): inviteSent = false when the person was saved but the email / text did not go (the server waits 8 s at most).
  // v12.10.26 (#2129): sendBy = Send invite by Email / Text / Both; delivery = what went, channel by channel, and why not.
  invite: (body: { email?: string; mobile?: string; groups: number[]; message?: string; sendBy?: InviteSendBy }, options?: RequestInit) =>
    api.post<InviteAnswer>(`${T2}/users/invite`, body, options),
  // v12.10.26 (#2132): email always + text when a mobile is on file; the answer says what went (never "That didn't work").
  resendInvite: (id: number) => api.post<InviteAnswer>(`${T2}/users/${id}/resend`),
  // v12.10.26 (#2133): a fresh one-time link, shown once (the earlier one stops working); audited on the server.
  inviteLink: (id: number) => api.post<{ link: string; expiresAt: string; row: UserRow | null }>(`${T2}/users/${id}/invite-link`),
  cancelInvite: (id: number) => api.post<void>(`${T2}/users/${id}/cancel-invite`),
  updateUser: (id: number, body: { firstName: string; lastName: string; email: string | null; mobile: string | null; jobTitle: string | null }) =>
    api.put<UserRow>(`${T2}/users/${id}`, body),
  setUserGroups: (id: number, groups: number[]) => api.put<UserRow>(`${T2}/users/${id}/groups`, { groups }),
  sendPasswordReset: (id: number) => api.post<void>(`${T2}/users/${id}/password-reset`),
  resetSecondStep: (id: number) => api.post<UserRow>(`${T2}/users/${id}/reset-2step`),
  unlock: (id: number) => api.post<UserRow>(`${T2}/users/${id}/unlock`),
  // v12.10.9.1 (#1292): why and when the account locked, before Unlock
  lockInfo: (id: number) => api.get<LockInfo>(`${T2}/users/${id}/lock-info`),
  signOutUser: (id: number) => api.post<void>(`${T2}/users/${id}/sign-out`),
  disable: (id: number) => api.post<UserRow>(`${T2}/users/${id}/disable`),
  enable: (id: number) => api.post<UserRow>(`${T2}/users/${id}/enable`),
  deleteUser: (id: number) => api.delete<void>(`${T2}/users/${id}`),

  groups: () => api.get<{ groups: Group[]; templates: GroupTemplate[] }>(`${T2}/groups`),
  createGroup: (body: { name: string; description?: string; fromGroupId?: number; templateKey?: GroupTemplate['key'] }) =>
    api.post<Group>(`${T2}/groups`, body),
  updateGroup: (id: number, body: { name?: string; description?: string; grants?: Grants }) => api.put<Group>(`${T2}/groups/${id}`, body),
  resetGroup: (id: number) => api.post<Group>(`${T2}/groups/${id}/reset`),
  deleteGroup: (id: number) => api.delete<void>(`${T2}/groups/${id}`),
  setGroupMembers: (id: number, userIds: number[]) => api.put<Group>(`${T2}/groups/${id}/members`, { userIds }),

  account: () => api.get<UserRow>(`${T2}/account`),
  savePhoto: (dataUrl: string) => api.post<{ photo: string }>(`${T2}/account/photo`, { dataUrl }),      // v12.10.9.3 (#1306)
  removePhoto: () => api.delete<{ photo: null }>(`${T2}/account/photo`),
  activity: () => api.get<{ items: ActivityItem[] }>(`${T2}/account/activity`),                       // v12.10.9.3 (#1307)
  updateAccount: (body: { firstName: string; lastName: string; mobile: string | null; jobTitle: string | null }) =>
    api.put<UserRow>(`${T2}/account`, body),
  // v12.10.25 (#2106): first-time user - the Welcome card, the Help guides' checklist progress, Show welcome again
  firstRun: () => api.get<FirstRunView>(`${T2}/account/first-run`),
  saveFirstRun: (body: { state?: FirstRunStateName; guide?: string; done?: string[] }) =>
    api.put<{ state: FirstRunStateName; progress: Record<string, string[]> }>(`${T2}/account/first-run`, body),
  resetFirstRun: (id: number) => api.post<void>(`${T2}/users/${id}/first-run-reset`),
};

// v12.10.25 (#2106): GET account/first-run
export type FirstRunStateName = 'new' | 'toured' | 'checklist' | 'skipped';
export interface FirstRunView {
  show: boolean; signIns: number; state: FirstRunStateName;
  firstName: string | null; practiceName: string | null;
  progress: Record<string, string[]>;
}

// ── §1 bearer-side auth endpoints used from inside the app ───────────────────────
export interface CodesResult { backupCodes: string[]; result: unknown | null }
export const accountAuthApi = {
  confirm: (body: { password: string } | { method: 'sms' | 'totp'; code: string }) =>
    api.post<{ confirmedUntil: string }>(`${AUTH}/confirm`, body),
  confirmSend: () => api.post<{ sent: boolean; maskedMobile: string }>(`${AUTH}/confirm/send`),
  changePassword: (oldPassword: string, newPassword: string) =>
    api.post<{ ok: boolean; signedOutOthers: number }>(`${AUTH}/change-password`, { oldPassword, newPassword }),
  totpStart: () => api.post<{ secret: string; otpauthUri: string }>(`${AUTH}/setup/totp/start`, {}),
  totpConfirm: (code: string) => api.post<CodesResult>(`${AUTH}/setup/totp/confirm`, { code }),
  smsStart: (mobile: string) => api.post<{ sent: boolean; maskedMobile: string; resendInSeconds: number }>(`${AUTH}/setup/sms/start`, { mobile }),
  smsConfirm: (code: string) => api.post<CodesResult>(`${AUTH}/setup/sms/confirm`, { code }),
  regenerateBackupCodes: () => api.post<{ backupCodes: string[] }>(`${AUTH}/backup-codes/regenerate`),
  passwordRules: () => api.get<{ minLength: number; reuse: number }>(`${AUTH}/password-rules`),
};

// ── error classification ───────────────────────────────────────────────────────
export interface SecurityErrorBody { error?: string; message?: string; area?: string; action?: string; failed?: string[]; triesLeft?: number; retryInSeconds?: number }

export function errorBody(err: unknown): SecurityErrorBody {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') return err.body as SecurityErrorBody;
  return {};
}
export function errorCode(err: unknown): string | undefined { return errorBody(err).error; }
export function isConfirmRequired(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 428 || errorCode(err) === 'confirm_required');
}
export function isForbidden(err: unknown): boolean { return err instanceof ApiError && err.status === 403; }
export function isEscalation(err: unknown): boolean { return err instanceof ApiError && err.status === 409 && errorCode(err) === 'escalation'; }
export function isLastAdmin(err: unknown): boolean { return err instanceof ApiError && err.status === 409 && errorCode(err) === 'last_admin'; }

/** The words shown inline (or in a toast) for a failed Security action. */
export function securityErrorMessage(err: unknown, fallback = 'That didn’t work. Try again.'): string {
  if (isEscalation(err)) return 'That would give more access than you have yourself. You can only give groups and access up to your own.';
  if (isLastAdmin(err)) return 'This is the last person who can manage users and groups. Give that to someone else first.';
  if (isForbidden(err)) return 'You don’t have access to do this. Ask someone who manages users and groups.';
  if (err instanceof ApiError && err.status === 401) return 'Your session ended. Sign in again in a new tab - what you typed is still here.';
  const b = errorBody(err);
  if (b.message) return b.message;
  return fallback;
}
